/**
 * Local Bridge hub: sessions grouped by company, Create Bridge Session, links to manage users/companies.
 * Scoped to the current workspace company (same id as Control Bridge). Uses backend Local Bridge API (PostgreSQL).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Button, Table, Spin, Alert, Space, Collapse, Typography, Dropdown } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  CheckOutlined,
  ControlOutlined,
  EditOutlined,
  FileTextOutlined,
  StarOutlined,
  PlayCircleOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import {
  listUsers as apiListUsers,
  getCompany,
  listBridgeSessions as apiListBridgeSessions,
  updateCompany,
  type LocalBridgeSession,
  type LocalUser,
  type LocalCompany,
} from '../services/localBridgeSessionApi';
import { CreateBridgeSessionWizard } from '../components/LocalBridge/CreateBridgeSessionWizard';
import { EditBridgeSessionObjectiveModal } from '../components/LocalBridge/EditBridgeSessionObjectiveModal';
import { useUser } from '../contexts/UserContext';
import { resolveWorkspaceCompanyId } from '../utils/resolveWorkspaceCompanyId';
import {
  BRIDGE_SESSION_OPEN_STEPS,
  continueActionLabel,
  resolveBridgeSessionContinuePath,
} from '../utils/bridgeSessionResumeRoutes';

const { Text } = Typography;

/** Sessions grouped by company_id, sorted by updated_at DESC within each company */
function groupSessionsByCompany(sessions: LocalBridgeSession[]): Map<string, LocalBridgeSession[]> {
  const byCompany = new Map<string, LocalBridgeSession[]>();
  for (const s of sessions) {
    const list = byCompany.get(s.company_id) ?? [];
    list.push(s);
    byCompany.set(s.company_id, list);
  }
  Array.from(byCompany.values()).forEach((list) => {
    list.sort((a: LocalBridgeSession, b: LocalBridgeSession) =>
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  });
  return byCompany;
}

export const LocalBridgePage: React.FC = () => {
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    selectedCompany,
    availableCompanies: companiesList = [],
    loadUserCompanies,
  } = useUser();
  const workspaceCompanyId = useMemo(
    () => resolveWorkspaceCompanyId(user?.companyId, selectedCompany?.id, companiesList),
    [user?.companyId, selectedCompany?.id, companiesList]
  );

  const [sessions, setSessions] = useState<LocalBridgeSession[]>([]);
  const [users, setUsers] = useState<LocalUser[]>([]);
  const [companies, setCompanies] = useState<LocalCompany[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [createWizardOpen, setCreateWizardOpen] = useState(false);
  const [editObjectiveSessionId, setEditObjectiveSessionId] = useState<string | null>(null);

  const enabled = isLocalBridgeApiEnabled();

  const usersMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const companiesMap = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const sessionsByCompany = useMemo(() => groupSessionsByCompany(sessions), [sessions]);

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void loadUserCompanies();
  }, [user?.id, isAuthenticated, loadUserCompanies]);

  /** Loads sessions and company row for the workspace only; users listed are those with sessions (or all if none yet, for first session). */
  const refreshBridgeData = useCallback(async () => {
    if (!enabled || !workspaceCompanyId) {
      setSessions([]);
      setUsers([]);
      setCompanies([]);
      return;
    }
    setLoadingSessions(true);
    try {
      const sess = await apiListBridgeSessions(workspaceCompanyId);
      setSessions(sess);
      const sessionUserIds = new Set(sess.map((s) => s.user_id));
      const [allUsers, companyRow] = await Promise.all([apiListUsers(), getCompany(workspaceCompanyId)]);
      setCompanies(companyRow ? [companyRow] : []);
      if (sessionUserIds.size === 0) {
        setUsers(allUsers);
      } else {
        setUsers(allUsers.filter((u) => sessionUserIds.has(u.id)));
      }
    } catch {
      setSessions([]);
      setUsers([]);
      setCompanies([]);
    } finally {
      setLoadingSessions(false);
    }
  }, [enabled, workspaceCompanyId]);

  useEffect(() => {
    void refreshBridgeData();
  }, [refreshBridgeData]);

  const handleOpenCreateWizard = () => {
    setCreateWizardOpen(true);
  };

  const handleWizardSessionCreated = () => {
    void refreshBridgeData();
  };

  /** View Control Bridge for company: use preferred session or last updated */
  const handleViewControlBridge = (companyId: string) => {
    const companySessions = sessionsByCompany.get(companyId) ?? [];
    const company = companiesMap.get(companyId);
    const sessionIds = new Set(companySessions.map((s) => s.id));
    const preferredSession =
      company?.control_bridge_session_id && sessionIds.has(company.control_bridge_session_id)
        ? companySessions.find((s) => s.id === company.control_bridge_session_id)
        : undefined;
    const session = preferredSession ?? companySessions[0];
    if (!session) return;
    navigate(`/dashboard?sessionId=${session.id}`);
  };

  const handleSetControlBridgeSession = async (companyId: string, sessionId: string | null) => {
    const updated = await updateCompany(companyId, {
      control_bridge_session_id: sessionId === null ? null : sessionId,
    });
    if (updated) {
      void refreshBridgeData();
    }
  };

  const handleViewBridgeCard = (sessionId: string) => {
    navigate(`/bridge-session/${sessionId}?step=bridge_card`);
  };

  const handleContinueSession = (session: LocalBridgeSession) => {
    navigate(resolveBridgeSessionContinuePath(session));
  };

  const openStepMenuItems = (sessionId: string): MenuProps['items'] =>
    BRIDGE_SESSION_OPEN_STEPS.map((step) => ({
      key: step.key,
      label: step.label,
      onClick: () => navigate(step.pathFor(sessionId)),
    }));

  const handleEdit = (sessionId: string) => {
    setEditObjectiveSessionId(sessionId);
  };

  const companyIds = useMemo(() => {
    const ids = Array.from(sessionsByCompany.keys());
    return ids.sort((a, b) => {
      const nameA = companiesMap.get(a)?.name ?? a;
      const nameB = companiesMap.get(b)?.name ?? b;
      return nameA.localeCompare(nameB);
    });
  }, [sessionsByCompany, companiesMap]);

  const sessionColumns: ColumnsType<LocalBridgeSession> = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => usersMap.get(r.user_id)?.name ?? r.user_id,
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      render: (v: string | undefined) => v ?? '—',
    },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'Control Bridge',
      key: 'control_bridge',
      render: (_, record) => {
        const company = companiesMap.get(record.company_id);
        const isActive = company?.control_bridge_session_id === record.id;
        return (
          <Space wrap>
            {isActive ? (
              <>
                <Text type="success">
                  <CheckOutlined /> Active
                </Text>
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleSetControlBridgeSession(record.company_id, null)}
                >
                  Clear
                </Button>
              </>
            ) : (
              <Button
                type="link"
                size="small"
                icon={<StarOutlined />}
                onClick={() => handleSetControlBridgeSession(record.company_id, record.id)}
              >
                Use for Control Bridge
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => handleContinueSession(record)}
          >
            {continueActionLabel(record)}
          </Button>
          <Dropdown menu={{ items: openStepMenuItems(record.id) }} trigger={['click']}>
            <Button size="small">
              Open step <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleViewBridgeCard(record.id)}
          >
            View Bridge Card
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.id)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const collapseItems = companyIds.map((companyId) => {
    const companySessions = sessionsByCompany.get(companyId) ?? [];
    const companyName = companiesMap.get(companyId)?.name ?? companyId;
    const company = companiesMap.get(companyId);
    const sessionIds = new Set(companySessions.map((s) => s.id));
    const preferredSession =
      company?.control_bridge_session_id && sessionIds.has(company.control_bridge_session_id)
        ? companySessions.find((s) => s.id === company.control_bridge_session_id)
        : undefined;
    const sessionForControlBridge = preferredSession ?? companySessions[0];

    return {
      key: companyId,
      label: (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>{companyName}</Text>
          <Space>
            <Text type="secondary">{companySessions.length} session(s)</Text>
            {sessionForControlBridge && (
              <Button
                type="primary"
                size="small"
                icon={<ControlOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewControlBridge(companyId);
                }}
              >
                View Control Bridge
              </Button>
            )}
          </Space>
        </Space>
      ),
      children: (
        <Table
          rowKey="id"
          columns={sessionColumns}
          dataSource={companySessions}
          pagination={false}
          size="small"
        />
      ),
    };
  });

  if (!enabled) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="info"
          message="Local Bridge API not configured"
          description="Local Bridge API is not available. Ensure the backend is running and REACT_APP_API_URL is configured."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1000 }}>
      <h1 style={{ marginBottom: 24 }}>Local Bridge</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Create bridge sessions, set which session is used for Control Bridge, then continue or jump to a
        step. Manage <Link to="/local-bridge/users">users</Link> and{' '}
        <Link to="/local-bridge/companies">companies</Link> in the backend.
      </p>

      <Card title="Bridge sessions by company" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" size="large" onClick={handleOpenCreateWizard}>
            Create Bridge Session
          </Button>
        </div>
        {loadingSessions ? (
          <Spin size="large" />
        ) : companyIds.length === 0 ? (
          <p style={{ color: '#6b7280' }}>No sessions yet. Create a bridge session to get started.</p>
        ) : (
          <Collapse items={collapseItems} defaultActiveKey={companyIds} />
        )}
      </Card>

      <div style={{ marginTop: 24, color: '#6b7280' }}>
        <Link to="/local-bridge/users" style={{ marginRight: 16 }}>
          Manage users
        </Link>
        <Link to="/local-bridge/companies">Manage companies</Link>
      </div>

      <CreateBridgeSessionWizard
        open={createWizardOpen}
        onClose={() => setCreateWizardOpen(false)}
        companyId={workspaceCompanyId}
        users={users}
        companies={companies}
        onUsersCompaniesChange={() => void refreshBridgeData()}
        onSessionCreated={handleWizardSessionCreated}
      />

      <EditBridgeSessionObjectiveModal
        sessionId={editObjectiveSessionId ?? ''}
        open={editObjectiveSessionId != null}
        onClose={() => setEditObjectiveSessionId(null)}
        onSaved={() => void refreshBridgeData()}
      />
    </div>
  );
};

export default LocalBridgePage;
